import { useState, useRef, useEffect, useCallback } from 'preact/hooks';
import {
  formatTime,
  normalizeWaveform,
  supportsBrowserCapture,
} from '../lib/mediaUtils';
import styles from './AudioContribution.module.css';
import sharedStyles from './ContributionShared.module.css';

export interface AudioContributionResult {
  blob: Blob;
  duration: number;
  waveform: number[];
  contentType: string;
  filename: string;
}

interface AudioContributionProps {
  onSubmit: (payload: AudioContributionResult) => void;
  submitting: boolean;
  promptText?: string;
  heading?: string;
  submitLabel?: string;
  discardLabel?: string;
  onUseFallbackCapture?: () => void;
  fallbackLabel?: string;
  onBack?: () => void;
}

type RecorderState = 'idle' | 'recording' | 'recorded' | 'permission-denied' | 'error' | 'unsupported';

const MAX_DURATION_SEC = 120;

function extensionForMimeType(mimeType: string): string {
  if (mimeType.includes('mp4')) return 'm4a';
  if (mimeType.includes('ogg')) return 'ogg';
  return 'webm';
}

export function AudioContribution({
  onSubmit,
  submitting,
  promptText,
  heading = 'Record Audio',
  submitLabel = 'Use Recording',
  discardLabel = 'Re-record',
  onUseFallbackCapture,
  fallbackLabel = 'Use Device Recorder',
  onBack,
}: AudioContributionProps) {
  const [state, setState] = useState<RecorderState>(() => supportsBrowserCapture() ? 'idle' : 'unsupported');
  const [elapsed, setElapsed] = useState(0);
  const [audioLevel, setAudioLevel] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [waveform, setWaveform] = useState<number[]>([]);
  const [recordingMimeType, setRecordingMimeType] = useState('audio/webm');
  const [countdownValue, setCountdownValue] = useState<number | null>(null);
  const countdownTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const animFrameRef = useRef<number | null>(null);
  const audioUrlRef = useRef<string | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const recordingSamplesRef = useRef<number[]>([]);
  const sampleCounterRef = useRef(0);

  useEffect(() => {
    return () => {
      stopRecording();
      if (countdownTimerRef.current) {
        clearTimeout(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }
      if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current);
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, []);

  const startLevelMonitor = useCallback((stream: MediaStream) => {
    try {
      const ctx = new AudioContext();
      audioCtxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);

      const data = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        analyser.getByteFrequencyData(data);
        const avg = data.reduce((sum, value) => sum + value, 0) / data.length;
        const normalized = Math.min(avg / 128, 1);
        setAudioLevel(normalized);
        sampleCounterRef.current += 1;
        if (sampleCounterRef.current % 6 === 0) {
          recordingSamplesRef.current.push(normalized);
        }
        animFrameRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch {
      // AudioContext may be unavailable. Recording still works without the level meter.
    }
  }, []);

  const stopLevelMonitor = useCallback(() => {
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
    if (audioCtxRef.current) {
      audioCtxRef.current.close().catch((err) => {
        console.warn('[AudioContribution] AudioContext close failed:', err);
      });
      audioCtxRef.current = null;
    }
    setAudioLevel(0);
  }, []);

  const startRecording = async () => {
    if (!supportsBrowserCapture()) {
      setState('unsupported');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : 'audio/mp4';

      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];
      recordingSamplesRef.current = [];
      sampleCounterRef.current = 0;
      setRecordingMimeType(mimeType);

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType });
        const nextUrl = URL.createObjectURL(blob);
        setAudioBlob(blob);
        setAudioUrl(nextUrl);
        audioUrlRef.current = nextUrl;
        setWaveform(normalizeWaveform(recordingSamplesRef.current));
        setState('recorded');
      };

      recorder.start(1000);
      setState('recording');
      setElapsed(0);

      timerRef.current = setInterval(() => {
        setElapsed((previous) => {
          const next = previous + 1;
          if (next >= MAX_DURATION_SEC) {
            stopRecording();
            return MAX_DURATION_SEC;
          }
          return next;
        });
      }, 1000);

      startLevelMonitor(stream);
    } catch (error: unknown) {
      const domError = error as DOMException;
      if (domError.name === 'NotAllowedError' || domError.name === 'PermissionDeniedError') {
        setState('permission-denied');
      } else {
        console.error('[AudioContribution] getUserMedia failed:', error);
        setState('error');
      }
    }
  };

  const stopRecording = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    stopLevelMonitor();
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  };

  const runCountdown = () => {
    let count = 3;
    setCountdownValue(count);
    const tick = () => {
      count--;
      if (count > 0) {
        setCountdownValue(count);
        countdownTimerRef.current = setTimeout(tick, 1000);
      } else {
        setCountdownValue(null);
        countdownTimerRef.current = null;
        startRecording();
      }
    };
    countdownTimerRef.current = setTimeout(tick, 1000);
  };

  const cancelCountdown = () => {
    if (countdownTimerRef.current) {
      clearTimeout(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    setCountdownValue(null);
  };

  const handleRecordClick = () => {
    if (countdownValue !== null) {
      cancelCountdown();
    } else if (state === 'recording') {
      stopRecording();
    } else {
      runCountdown();
    }
  };

  const discard = () => {
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
    }
    audioUrlRef.current = null;
    setAudioBlob(null);
    setAudioUrl(null);
    setElapsed(0);
    setWaveform([]);
    setState(supportsBrowserCapture() ? 'idle' : 'unsupported');
  };

  const handleSubmit = (event: Event) => {
    event.preventDefault();
    if (!audioBlob || submitting) return;

    const contentType = recordingMimeType || audioBlob.type || 'audio/webm';
    onSubmit({
      blob: audioBlob,
      duration: elapsed,
      waveform,
      contentType,
      filename: `voice-${Date.now()}.${extensionForMimeType(contentType)}`,
    });
  };

  if (state === 'unsupported') {
    return (
      <div class={sharedStyles.permissionError}>
        <h3 class={sharedStyles.heading}>Browser recording isn&apos;t available here</h3>
        <p>
          Use your device recorder instead, then attach the file back in the composer.
        </p>
        {onUseFallbackCapture && (
          <button type="button" onClick={onUseFallbackCapture}>{fallbackLabel}</button>
        )}
      </div>
    );
  }

  if (state === 'permission-denied') {
    return (
      <div class={sharedStyles.permissionError}>
        <h3 class={sharedStyles.heading}>Microphone Access Required</h3>
        <p>
          We need microphone access to record audio. Please allow microphone access in your browser
          settings and try again.
        </p>
        <div class={styles.fallbackActions}>
          <button type="button" onClick={() => setState('idle')}>Try Again</button>
          {onUseFallbackCapture && (
            <button type="button" onClick={onUseFallbackCapture}>{fallbackLabel}</button>
          )}
        </div>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div class={sharedStyles.errorState}>
        <h3 class={sharedStyles.heading}>Something Went Wrong</h3>
        <p>
          We couldn&apos;t access your microphone. Please check that a microphone is connected
          and try again.
        </p>
        <div class={styles.fallbackActions}>
          <button type="button" onClick={() => setState('idle')}>Try Again</button>
          {onUseFallbackCapture && (
            <button type="button" onClick={onUseFallbackCapture}>{fallbackLabel}</button>
          )}
        </div>
      </div>
    );
  }

  if (state === 'recorded' && audioUrl) {
    return (
      <form onSubmit={handleSubmit} class={sharedStyles.captureRoot}>
        {onBack && (
          <button type="button" class={`${sharedStyles.floatingBack} ${styles.floatingBack}`} onClick={onBack} aria-label="Go back">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
          </button>
        )}
        <h3 class={`${sharedStyles.heading} ${sharedStyles.headingWithBack}`}>Review Recording</h3>
        <div class={styles.playbackArea}>
          <span class={sharedStyles.srOnly}>Audio waveform</span>
        <div class={styles.waveformReview} aria-hidden="true">
            {waveform.map((sample, index) => (
              <span
                key={`${sample}-${index}`}
                class={styles.waveformBar}
                style={{ height: `${Math.max(12, sample * 100)}%` }}
              />
            ))}
          </div>

          <audio
            src={audioUrl}
            controls
            preload="metadata"
            class={styles.audioPlayback}
          />

          <div class={styles.recordingMeta}>
            <span>{formatTime(elapsed)} / {formatTime(MAX_DURATION_SEC)}</span>
            <span>Voice note ready</span>
          </div>

          <div class={sharedStyles.actions}>
            <button type="button" class={sharedStyles.discardButton} onClick={discard}>
              {discardLabel}
            </button>
            <button type="submit" class={sharedStyles.submitButton} disabled={submitting}>
              {submitting ? 'Uploading...' : submitLabel}
            </button>
          </div>
        </div>
      </form>
    );
  }

  return (
    <div class={sharedStyles.captureRoot}>
      {onBack && (
        <button type="button" class={`${sharedStyles.floatingBack} ${styles.floatingBack}`} onClick={onBack} aria-label="Go back">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
        </button>
      )}
      <h3 class={`${sharedStyles.heading} ${onBack ? sharedStyles.headingWithBack : ''}`}>{heading}</h3>
      <div class={styles.recorderArea}>
        {promptText && (
          <div class={styles.promptCard}>
            <span class={styles.promptIcon}>💡</span>
            <span class={styles.promptText}>{promptText}</span>
          </div>
        )}

        <span class={styles.timer}>
          {formatTime(elapsed)} / {formatTime(MAX_DURATION_SEC)}
        </span>

        {state === 'recording' && (
          <div class={styles.levelBar}>
            <div class={styles.levelFill} style={{ width: `${audioLevel * 100}%` }} />
          </div>
        )}

        <span class={sharedStyles.srOnly} aria-live="assertive">
          {state === 'recording' ? 'Recording in progress' : ''}
        </span>

        <div class={styles.captureArea}>
          {countdownValue !== null && (
            <div class={styles.countdownOverlay} role="status" aria-live="assertive">
              <span class={styles.countdownNumber} key={countdownValue}>{countdownValue}</span>
            </div>
          )}

          <button
            type="button"
            aria-label={
              countdownValue !== null
                ? 'Cancel countdown'
                : state === 'recording'
                  ? 'Stop recording audio'
                  : 'Start recording audio'
            }
            class={`${sharedStyles.recordButton} ${state === 'recording' ? sharedStyles.recording : ''}`}
            onClick={handleRecordClick}
          >
            {state === 'recording' ? (
              <span class={sharedStyles.stopIcon} />
            ) : (
              <span class={sharedStyles.recordIcon} />
            )}
          </button>
        </div>

        <span class={sharedStyles.hint}>
          {countdownValue !== null
            ? 'Tap to cancel'
            : state === 'recording'
              ? 'Tap to stop'
              : 'Tap to record'}
        </span>
      </div>
    </div>
  );
}
